import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgentService, AgentDto, AssignAgentZoneDto, ZoneDto } from '../../../services/agent.service';

@Component({
  selector: 'app-assign-agent-zone',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './assign-agent-zone.component.html',
  styleUrl: './assign-agent-zone.component.css'
})
export class AssignAgentZoneComponent implements OnInit {
  @Input() agent!: AgentDto;
  @Input() zones: ZoneDto[] = [];
  @Output() zoneAssigned = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();

  selectedZoneId: string = '';
  agentZones: ZoneDto[] = [];
  isSubmitting = false;
  isLoadingAgentZones = false;
  errors: { [key: string]: string } = {};

  constructor(private agentService: AgentService) {}

  ngOnInit(): void {
    this.loadAgentZones();
  }

  loadAgentZones(): void {
    if (!this.agent?.agentId) {
      return;
    }

    this.isLoadingAgentZones = true;
    this.agentService.getAgentZones(this.agent.agentId).subscribe({
      next: (zones) => {
        this.agentZones = zones;
        this.isLoadingAgentZones = false;
      },
      error: (error) => {
        console.error('Error loading agent zones:', error);
        this.agentZones = [];
        this.isLoadingAgentZones = false;
      }
    });
  }

  getAvailableZones(): ZoneDto[] {
    if (!this.agent?.zones || this.agent.zones.length === 0) {
      return this.zones;
    }
    
    // Filter out zones already assigned to the agent
    return this.zones.filter(zone => !this.agent.zones.includes(zone.id));
  }

  isZoneAssigned(zoneId: string): boolean {
    return this.agent?.zones?.includes(zoneId) || false;
  }

  getZoneName(zoneId: string): string {
    const zone = this.zones.find(z => z.id === zoneId);
    return zone ? zone.name : zoneId;
  }

  validateForm(): boolean {
    this.errors = {};

    if (!this.selectedZoneId) {
      this.errors['zone'] = 'Please select a zone to assign';
      return false;
    }

    if (this.isZoneAssigned(this.selectedZoneId)) {
      this.errors['zone'] = 'This zone is already assigned to the agent';
      return false;
    }

    return true;
  }

  onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isSubmitting = true;

    const assignData: AssignAgentZoneDto = {
      agentId: this.agent.agentId,
      zoneId: this.selectedZoneId
    };

    this.agentService.assignAgentZone(assignData).subscribe({
      next: (response) => {
        this.zoneAssigned.emit();
        this.resetForm();
      },
      error: (error) => {
        console.error('Error assigning zone:', error);
        this.handleServerErrors(error);
        this.isSubmitting = false;
      }
    });
  }

  private handleServerErrors(error: any): void {
    this.errors = {};

    if (error?.error?.errors) {
      // Handle validation errors from the server
      Object.keys(error.error.errors).forEach(key => {
        const fieldName = key.toLowerCase();
        if (error.error.errors[key] && Array.isArray(error.error.errors[key])) {
          this.errors[fieldName] = error.error.errors[key][0];
        } else if (error.error.errors[key]) {
          this.errors[fieldName] = error.error.errors[key];
        }
      });
    } else if (error?.error?.message) {
      this.errors['general'] = error.error.message;
    } else if (error?.message) {
      this.errors['general'] = error.message;
    } else {
      this.errors['general'] = 'An unexpected error occurred. Please try again.';
    }
  }

  onCancel(): void {
    this.closeModal.emit();
    this.resetForm();
  }

  private resetForm(): void {
    this.selectedZoneId = '';
    this.errors = {};
    this.isSubmitting = false;
  }

  // Helper method to check if a field has an error
  hasError(fieldName: string): boolean {
    return !!this.errors[fieldName];
  }

  // Helper method to get error message for a field
  getError(fieldName: string): string {
    return this.errors[fieldName] || '';
  }

  // Handle modal backdrop click
  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  // Get formatted creation date
  getFormattedDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }
}
